import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';
import { Html5Qrcode } from 'html5-qrcode';

const SCANNER_CONFIG = { fps: 10, qrbox: { width: 260, height: 260 } };

/**
 * Camera QR scanner modal for joining a live quiz session.
 */
const QuizQrScanner = ({ open, onClose, onScan }) => {
    const readerId = useId().replace(/:/g, '');
    const scannerRef = useRef(null);
    const onScanRef = useRef(onScan);
    const scannedRef = useRef(false);
    const [readerEl, setReaderEl] = useState(null);
    const [error, setError] = useState('');

    onScanRef.current = onScan;

    const readerRefCallback = useCallback((node) => {
        setReaderEl(node);
    }, []);

    const stopScanner = useCallback(async () => {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (!scanner) return;
        try {
            if (scanner.isScanning) {
                await scanner.stop();
            }
            scanner.clear();
        } catch {
            // ignore cleanup errors
        }
    }, []);

    useEffect(() => {
        if (!open) {
            scannedRef.current = false;
            stopScanner();
            setError('');
            return undefined;
        }

        if (!readerEl) {
            return undefined;
        }

        let cancelled = false;
        scannedRef.current = false;

        const handleDecode = async (decodedText) => {
            if (cancelled || scannedRef.current) return;
            scannedRef.current = true;
            await stopScanner();
            onScanRef.current(decodedText);
        };

        const start = async () => {
            const el = document.getElementById(readerId);
            if (!el) {
                if (!cancelled) {
                    setError('Scanner could not initialize. Please try again.');
                }
                return;
            }

            const scanner = new Html5Qrcode(readerId);
            scannerRef.current = scanner;

            try {
                await scanner.start(
                    { facingMode: 'environment' },
                    SCANNER_CONFIG,
                    handleDecode,
                    () => {}
                );
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.message?.includes('Permission')
                            ? 'Camera permission denied. Allow camera access or enter the code manually.'
                            : 'Unable to start camera. Enter the quiz code manually.'
                    );
                }
            }
        };

        // Wait for modal portal paint before attaching camera
        const frameId = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) start();
            });
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(frameId);
            stopScanner();
        };
    }, [open, readerEl, readerId, stopScanner]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                sx={{
                    maxWidth: 420,
                    width: '100%',
                    '&:hover': { borderColor: '#ea7a23' },
                    '&:focus-within': { borderColor: '#ea7a23' }
                }}
            >
                <ModalClose />
                <Typography level="title-lg" sx={{ mb: 0.5 }}>
                    Scan quiz QR code
                </Typography>
                <Typography level="body-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                    Point your camera at the QR code shown by your faculty.
                </Typography>

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    ref={readerRefCallback}
                    id={readerId}
                    sx={{
                        width: '100%',
                        minHeight: 280,
                        borderRadius: 'sm',
                        overflow: 'hidden',
                        bgcolor: 'background.level1',
                        '& video': { borderRadius: 'sm' }
                    }}
                />
            </ModalDialog>
        </Modal>
    );
};

export default QuizQrScanner;
